/**
 * Canonical English translation bundle. This file is the source of truth for
 * the shape of all locale bundles: the {@link Messages} type is derived from
 * it, so adding a key here forces every other locale to provide a
 * translation at compile time.
 *
 * Conventions:
 * - Group keys by feature area (`thumbs`, `appointment`, `blocks`, …) so
 *   related strings stay together and lookups stay readable.
 * - Use `{name}`-style placeholders for variable substitution; consumers
 *   pass `vars` to `t()` to fill them in.
 */
export const enMessages = {
  common: {
    languageUpdateFailed: 'Failed to update language',
    unknownPatient: 'Unknown Patient',
    cancel: 'Cancel',
    confirm: 'Confirm',
    somethingWentWrong: 'Something went wrong. Please try again.',
    copyToClipboard: 'Copy to clipboard',
    copyWithText: 'Copy: {text}',
    copied: 'Copied!',
  },
  widgetHeader: {
    settings: 'Settings',
    accountSettings: 'Account Settings',
    customizeInsights: 'Customize Insights',
    helpAndFeedback: 'Help & Feedback',
    openInEluve: 'Open in Eluve',
    hide: 'Minimize',
    testSentry: 'Test Sentry',
    language: 'Language',
  },
  thumbs: {
    startRecording: 'Start Recording',
    signIn: 'Sign In',
    openDetails: 'Open Details',
    pause: 'Pause',
    resume: 'Resume',
    generateNote: 'Generate Note',
    recordingTooShortTitle: 'Recording too short',
    recordingTooShortBody:
      'Need at least 10 seconds of audio to generate a note. Keep recording, then try again.',
    somethingWentWrong: 'Something went wrong',
    generatingNote: 'Generating note...',
    summaryAvailable: 'Summary Available',
    summaryFailed: 'Summary Failed',
    syncedOn: 'Synced on {date}',
  },
  thumbsV2: {
    signedOut: 'Signed out',
    signIn: 'Sign in',
    welcome: 'Welcome',
    ready: 'Ready to record',
    recording: 'Recording',
    paused: 'Paused',
    writingNote: 'Writing note…',
    noteReady: 'Note ready',
    completed: 'Completed',
    microphoneRequired: 'Microphone required',
    microphoneDisconnected: 'Microphone disconnected',
    recordingTooShort: 'Recording too short',
    notEnoughInfo: 'Not enough information',
    somethingWentWrong: 'Something went wrong',
    summaryFailed: 'Summary failed',
    startRecording: 'Start recording',
    finish: 'Finish',
    pause: 'Pause',
    recordMore: 'Record more',
    sendToEmr: 'Send to EMR',
    sendToEmrAgain: 'Send to EMR again',
    allowAccess: 'Allow access',
    showDetails: 'Maximize',
    microphone: 'Microphone',
    settings: 'Appointment settings',
    seeNotesTab: 'See Notes tab for details',
    canceled: 'Appointment canceled',
  },
  appointment: {
    noSummaryAvailableTitle: 'No summary available yet',
    noSummaryAvailableDescription:
      'Your note will be generated here once the session is completed.',
    insightsTab: 'Insights',
    summaryTab: 'Summary',
    notesTab: 'Notes',
    transcriptTab: 'Transcript',
    soapNotesTab: 'SOAP Notes',
    notFound: 'Appointment not found.',
    canceledInEhr: 'This appointment was canceled in your EHR.',
    grantMicrophonePermission: 'Grant Microphone Permission',
    noMicrophoneSelected: 'No microphone selected',
    uploading: 'Uploading Recording',
    transcript: {
      empty: 'No transcript.',
      viewTranscript: 'View Transcript',
      hideTranscript: 'Hide Transcript',
      sessionStartedAt: 'Session started {date} at {time}',
      sessionResumedAt: 'Session resumed {date} at {time}',
      sessionPausedAt: 'Session paused {date} at {time}',
    },
    transition: {
      title: 'You have an ongoing session for a different appointment',
      description: 'End the current session in order to continue.',
      endSession: 'End Session',
      sessionPausedToast: 'Your session has been paused',
      sessionPausedToastDescription:
        "Return to {patientName}'s appointment to resume or to generate a note.",
    },
    presence: {
      title: 'Are you still here?',
      description:
        'This session is longer than usual. It is going to automatically pause in',
      pauseNow: 'Pause Now',
      continueRecording: 'Continue Recording',
    },
    onboarding: {
      title: 'Ready to record your first appointment on Eluve?',
      subtitle: 'Tell us a bit about yourself to personalize your experience.',
      uiLanguageLabel: 'What language should Eluve be in?',
      uiLanguageTooltip:
        'You can change this any time from the settings menu in the header.',
      uiLanguageAriaLabel: 'UI Language',
      uiLanguageSearchPlaceholder: 'Search languages...',
      specialtyLabel: 'What is your specialty?',
      specialtyTooltip:
        'This helps us tailor note templates and terminology to your specific practice area.',
      specialtyPlaceholder: 'Select specialty...',
      inputLanguageLabel: 'In what language do you speak to your patients?',
      inputLanguageTooltip:
        'This helps improve transcription accuracy by optimizing for your spoken language.',
      inputLanguageAriaLabel: 'Spoken Language',
      outputLanguageLabel: 'In what language do you write your SOAP notes?',
      outputLanguageAriaLabel: 'Note Language',
      save: 'Save',
      saveFailed: 'Failed to save preferences. Please try again.',
    },
    audioFile: {
      unsupportedFormat: 'Unsupported format: .{extension}. Use {formats}.',
      tooLarge: 'File too large ({size}MB). Max 100MB.',
      uploaded: 'Audio file uploaded. Processing will begin shortly.',
      processFailed: 'Failed to process audio file.',
      processing: 'Processing...',
      dropHere: 'Drop audio file to process',
    },
    session: {
      unavailableTitle: 'Session Unavailable',
      unavailableDescription:
        'Your session could not be loaded. Click Retry to restore it.',
      retry: 'Retry',
    },
    languageSelector: {
      regenerateFailed: 'Failed to regenerate summary. Please try again.',
      regeneratingToast: 'Regenerating your summary in the new language',
      regeneratingToastDescription: 'Your new summary should be available soon',
      spokenLanguageWarning:
        "Changing the spoken language will re-transcribe the entire conversation from the session's start.",
      notesLanguageWarning:
        'Changing the notes language will generate a new note',
      notesLanguageWarningDescription:
        'The new note will be generated in {language}.',
      notesLanguageWarningOverwrite: 'This will overwrite the current summary.',
      theSelectedLanguage: 'the selected language',
      spokenLanguageLabel: 'Spoken Language:',
      spokenLanguageAriaLabel: 'Spoken Language',
      notesLanguageLabel: 'Notes Language:',
      notesLanguageAriaLabel: 'Notes Language',
    },
    signedSummary: {
      amendmentFailed: 'Failed to create amendment',
      additionalNotes: 'Additional Notes',
    },
    summaryResult: {
      hideWindowTitle: 'You can minimize this window',
      hideWindowDescription:
        'To minimize this window without losing your progress, click the Minimize button in the top right.',
    },
    summarization: {
      noSummaryAvailable: 'No summary available for this appointment',
    },
    summaryFailed: {
      resumeRecording: 'Resume recording',
      regenerateNote: 'Try again',
    },
    recordingError: {
      refreshPage: 'Refresh the Page',
      tryAgain: 'Try Again',
      insufficientStorageCta: 'View session',
    },
    micDisconnected: {
      resumeRecording: 'Resume Recording',
    },
    insights: {
      differentialDiagnoses: 'Differential Diagnoses',
      generate: 'Generate',
      generateBlockedTooltip:
        'Pre-Visit Insights are only generated for upcoming or in-progress visits.',
      blockedTitle: 'Pre-Visit Insights unavailable',
      blockedMessage:
        "This appointment is no longer upcoming, so new Pre-Visit Insights aren't generated.",
      title: 'AI Insights',
      tooltip:
        'AI-generated insights based on patient history to help inform your clinical decisions.',
      collapse: 'Collapse',
      show: 'Show',
      loading: {
        reviewingPriorNotes: "Reviewing patient's prior notes…",
        summarizingDocumentation: 'Summarizing documentation…',
        gatheringInsights: 'Gathering insights…',
      },
      pvi: {
        noDataTitle: 'No previous notes for this patient',
        noDataMessage:
          'Eluve has no previous data on this patient. Start recording your appointment to generate your first note for this patient.',
        errorTitle: 'Could not generate AI insights',
        errorMessage:
          "Eluve couldn't generate insights for this patient. You can still record and summarize your appointment.",
      },
    },
    ddx: {
      couldNotGenerate: 'Could not generate DDx',
      couldNotGenerateDescription:
        "Eluve couldn't generate differential diagnoses for this appointment.",
      notGenerated:
        'No diagnoses available yet. Please check back after recording and summarizing your appointment.',
      empty: 'No differential diagnoses were identified.',
      considerations: 'CONSIDERATIONS',
      feedbackPrompt: 'Were these diagnoses helpful?',
      loading: {
        analyzing: 'Analyzing clinical context…',
        identifying: 'Identifying differential diagnoses…',
        evaluating: 'Evaluating diagnostic evidence…',
      },
    },
    feedback: {
      yes: 'Yes',
      no: 'No',
      additionalFeedback: 'Any additional feedback?',
      ratingPrompt:
        'How would you rate your Eluve experience for this session?',
      ratingPoor: 'Poor',
      ratingOkay: 'Okay',
      ratingExcellent: 'Excellent',
      tellMore: 'Tell us more!',
      submit: 'Submit Feedback',
      wasNoteHelpful: 'Was this note helpful?',
      summarySaveFailed: 'Failed to save feedback',
      summaryUpdateFailed: 'Failed to update feedback',
      summaryTagsUpdateFailed: 'Failed to update feedback tags',
      thanksPositive: 'Thanks for the feedback! Glad you liked this one!',
      thanksNegative:
        'Sorry that this summary missed the mark. Every piece of feedback you give (positive or negative) helps us improve our future results.',
      wasHelpful: 'Was this helpful?',
      shareFeedback: 'Share feedback',
      tellMoreOptional: 'Tell us more (optional)',
      submitShort: 'Submit',
      closeForm: 'Close feedback form',
      thumbsUp: 'Thumbs up',
      thumbsDown: 'Thumbs down',
      tagTooLong: 'Too long',
      tagWrongInformation: 'Wrong information',
      tagTooSlow: 'Too slow',
      tagNotEnoughInformation: 'Not enough information',
      tagOtherIssue: 'Other issue',
      tagMissingDetails: 'Missing important details',
      tagIrrelevantDetails: 'Contains irrelevant details',
      tagUnspokenDetails: 'Contains unspoken details',
      tagIncorrectNumber: 'Incorrect number',
      tagIncorrectBodyPart: 'Incorrect body part',
      tagBadFormatting: 'Bad formatting',
      tagTooVerbose: 'Too verbose',
    },
    summary: {
      copiedToClipboard: 'Copied summary to clipboard',
      copySummary: 'Copy Summary',
    },
    signNote: {
      onlyOwner: 'Only the appointment owner may sign this note.',
      chartNotModified:
        "The chart hasn't been modified. Please fill out the chart before signing the note.",
      selectLocation: 'Select a location before signing the note.',
      selectPatient: 'Select a patient before signing the note.',
      label: 'Sign Note',
      confirmTitle: 'Are you sure?',
      confirmDescription:
        'You will no longer be able to edit this note in Eluve.',
      confirmLabel: 'Yes, Sign Note',
    },
    syncEhr: {
      syncFailed: 'Sync Failed',
      syncedSuccessfully: 'Synced Successfully!',
      notAvailableLocation: 'Note syncing is not available for your location.',
      notOwner: 'Only the appointment owner may sync this note.',
      chartNotModified:
        "The chart hasn't been modified. Please fill out the chart before syncing the note.",
      selectPatient: 'Select a patient before syncing the note.',
      selectLocation: 'Select a location before syncing the note.',
      notAvailableNow: 'Note syncing is not available right now.',
      pushToEmr: 'Push to EMR',
      pushAgainNotAvailable:
        "This note has already been sent to your EHR and can't be pushed again.",
      syncAgain: 'Sync Again',
      syncToEhr: 'Sync to EHR',
    },
    settings: {
      spokenLanguageDescription:
        'Changing the spoken language will re-transcribe the entire conversation, from the start of the session.',
      retranscribeSession: 'Re-transcribe Session',
      notesLanguageDescription:
        'This will regenerate the note in the selected language, and overwrite the current one.',
      regenerateNote: 'Regenerate Note',
      templateDescription:
        'This will regenerate the note using the selected template, and overwrite the current one.',
      filterPlaceholder: 'Type to filter...',
      noLanguagesFound: 'No languages found.',
      noTemplatesFound: 'No templates found.',
      templateUpdateFailed: 'The template could not be updated.',
      spokenLanguageUpdateFailed: 'The spoken language could not be updated.',
      noteLanguageUpdateFailed: 'The note language could not be updated.',
      spokenLanguageItem: 'Spoken language: {language}',
      noteLanguageItem: 'Note language: {language}',
      templateItem: 'Template: {template}',
      notSelected: 'Not selected',
      testSentry: 'Test Sentry',
      spokenLanguageHeading: 'Spoken language',
      noteLanguageHeading: 'Note language',
      templateHeading: 'Template',
      confirmTitle: 'Are you sure?',
    },
    summaryStates: {
      pendingDataTitle: 'Preparing note generation...',
      pendingDataDescription:
        "We are still uploading and processing some files. Once done, we'll generate your note.",
      inProgressTitle: 'Generating note...',
      inProgressDescription:
        "You can leave this page and start a new session! We'll let you know when your note is ready.",
      takingLongerTitle: 'This is taking longer than expected...',
      takingLongerDescription:
        "We're still working on your note. You can leave this page and start a new session! We'll let you know when it is ready.",
      regenerateHint:
        'You can try regenerating the note using a different template.',
    },
    contentStates: {
      notFoundTitle: 'Appointment not found',
      notFoundDescription:
        "We couldn't find this appointment in your EMR. Try opening a different appointment.",
      canceledTitle: 'Appointment canceled',
      canceledDescription:
        'This appointment was canceled in your EMR. Try opening a different appointment.',
      notStartedTitle: 'Ready to record',
      notStartedDescription: 'Press "Start recording" to begin.',
      recordingTitle: 'Recording in progress',
      recordingDescription:
        'Speak naturally. Describe the history, symptoms, and your clinical assessment.',
      pausedTitle: 'Recording paused',
      pausedDescription:
        'Press "Record more" to continue, or "Finish" to write the note.',
      pendingAudioTitle: 'Uploading recording…',
      pendingAudioDescription: 'This will only take a moment.',
      summarizingTitle: 'Writing note…',
      summarizingDescription: 'This usually takes under a minute.',
      takingLongerTitle: 'This is taking longer than usual…',
      takingLongerDescription:
        "We're still writing your note. You can leave this page. We'll let you know when it's ready.",
      stuckTitle: "Your recording didn't go through",
      stuckDescription: 'The upload was interrupted. Try again.',
      degradedTranscriptTitle: "Some audio couldn't be processed",
      degradedTranscriptDescription: 'Try again. This usually fixes it.',
      noteTooShortTitle: 'Recording too short',
      noteTooShortDescription:
        'We only caught a few words from your recording. To write an accurate note, press "Record more" and describe the clinical details.',
      noteNoContextTitle: 'Not enough clinical detail',
      noteNoContextDescription:
        'To write an accurate note, press "Record more" and describe the clinical details. Like symptoms, diagnosis, or plan.',
      failedTitle: 'Something went wrong',
      failedDescription:
        'Our engineers know and are looking into it. Please try again.',
      templateFailedTitle: "This template didn't work",
      templateFailedDescription:
        "We couldn't write your note. Select a different template below, then try again.",
      micPermissionTitle: 'Microphone access needed',
      micPermissionDescription: 'Eluve needs your microphone to record audio.',
      micDisconnectTitle: 'Microphone disconnected',
      micDisconnectDescription:
        'Recording paused. Choose a microphone, then press "Record more".',
      recordingErrorTitle: 'Recording interrupted',
      recordingErrorDescription: 'Try again or refresh the page.',
      sessionExpiredTitle: 'Session expired',
      sessionExpiredDescription:
        'Your recording is saved. Refresh the page to continue.',
      lowStorageTitle: 'Your device is low on storage',
      lowStorageDescription:
        'Free up space on your computer to keep recording. What you recorded is saved.',
      recordingErrorRefresh: 'Refresh',
      recordingErrorRetry: 'Try again',
      recordingErrorDismiss: 'Dismiss',
    },
    summarizationError: {
      somethingWrong: 'Something went wrong',
      failedToGenerate: 'Failed to generate note',
      teamInvestigating:
        'Our team is investigating the issue. You can try again, or email support@eluve.com if you need assistance.',
      recordingTooShort:
        "This recording is too short or doesn't have enough clinical information to generate a note. Resume recording to add more content.",
      templateSelectionRequired:
        'Note generation failed. Select a different template below and click Regenerate to try again.',
    },
    summaryStuck: {
      description:
        "We couldn't generate your note. You can try again, or email support@eluve.com if you need assistance.",
      retryButton: 'Try Again',
      retrySuccessTitle: 'Trying again',
      retrySuccessDescription: 'Your note should be available soon',
      retryErrorTitle: 'Failed to retry',
      retryErrorDescription: 'Please try again or contact support',
    },
    regenerate: {
      button: 'Regenerate',
      title: 'Regenerating your summary',
      overwriteWarning: 'This will overwrite the current summary.',
      templateLabel:
        'Your new note will be generated with the following template:',
      successDescription: 'Your new summary should be available soon',
      errorTitle: 'Failed to regenerate summary',
      errorDescription: 'Please try again',
    },
    disabledTooltips: {
      noMicTitle: 'No Microphone Selected',
      noAudioTitle: 'No Audio Detected',
      noMicDescription: 'Please select one to start recording.',
      noAudioDescription:
        'Please unmute your microphone or select a different one to start recording.',
      storageFullTitle: 'Not enough storage',
      storageFullDescription:
        'Your device is low on storage, which can stop a recording from saving. Free up space and try again.',
      recordingTooShortTitle: 'Recording too short',
      recordingTooShortDescription:
        'We need at least 10 seconds of audio to write a note. Please keep recording.',
    },
    completed: {
      synced: 'Synced',
      syncedWithDate: 'Synced: {date}',
    },
    active: {
      pausedWithDuration: 'Paused - {duration}',
      pausedWithDurationDot: 'Paused · {duration}',
      pauseRecording: 'Pause Recording',
      resumeRecording: 'Resume Recording',
      paused: 'Paused',
      recording: 'Recording...',
    },
  },
  blocks: {
    transcriptionWarning: {
      title: 'Live transcription has been paused.',
      description:
        'Your recording is still being saved. The transcript will be completed after the appointment.',
    },
    language: {
      searchPlaceholder: 'Search languages...',
    },
    specialty: {
      defaultPlaceholder: 'Select a specialty',
      other: 'Other',
      clearSpecialty: 'Clear specialty',
      enterSpecialty: 'Enter your specialty',
      searchPlaceholder: 'Search specialties...',
    },
    callout: {
      dismissTip: 'Dismiss tip: {title}',
      dontShowAgain: "Don't show this again",
    },
  },
  errors: {
    homeScreenNotFound: {
      title: 'Home screen unavailable',
      message:
        "We're sorry, but we couldn't load EHR home screen. Please try refreshing the page.",
    },
    notLoggedIn: {
      title: 'Not authorized',
      message: 'Please sign in to your EHR account in a new tab and try again.',
    },
    soapTemplateNotFound: {
      title: 'Template missing',
      message:
        'Template not found: "SOAP Template". Please check if the template exists and try again.',
    },
    noteEditorNotFound: {
      title: 'Note editor unavailable',
      message:
        "We couldn't locate the note editor within EHR. Please ensure it's present and try again.",
    },
    templateNotSupported: {
      title: 'Template not supported',
      message:
        'The selected template is not supported by the EHR. Please select a different template and try again.',
    },
    failedToSign: {
      title: 'Chart entry signature failed',
      message:
        'We encountered an issue while signing the chart entry. Please try again.',
    },
    genericError: {
      title: 'Something went wrong',
      message:
        'Our team is investigating the issue. You can try again, or email support@eluve.com if you need assistance.',
    },
    timeout: {
      title: 'Request timed out',
      message:
        'Your request took too long to process. Please check your internet connection and try again.',
    },
    failedToImport: {
      title: 'Import failed',
      message: 'Failed to import data. Please check the data and try again.',
    },
    staffMismatch: {
      title: 'Appointment owner mismatch',
      message:
        'The appointment owner does not match the current user in EHR. Please ensure you are logged in as the correct user and try again.',
    },
    invalidStatePageRefreshRequired: {
      title: 'Session expired',
      message:
        "We've noticed you're in an inconsistent state. Please refresh the page to continue.",
    },
    micDisconnected: {
      title: 'Microphone not detected',
      message:
        'Your recording has been paused. Please update your microphone selection in order to resume.',
    },
    micPermissionsDenied: {
      title: 'Microphone access required',
      message:
        'Eluve needs permission to access your microphone to record audio.',
    },
    domInjectionFailed: {
      title: "That didn't work",
      message: 'Try copying the notes section by section.',
    },
    patientDeleted: {
      title: 'Patient not found in EHR',
      message:
        'The patient associated with this appointment has been deleted in your EHR. Please ensure the patient exists or select the correct patient in Eluve and try again.',
    },
    appointmentNotFound: {
      title: 'Appointment not found in EHR',
      message:
        'The appointment may have been deleted or rescheduled in your EHR. Please verify the appointment exists and try again.',
    },
    userIdMismatch: {
      title: 'User mismatch detected',
      message:
        'The logged-in user does not match the current EHR user. Re-authenticating automatically.',
    },
    extensionConnectionLost: {
      title: 'Extension connection lost',
      message:
        'The connection to the Eluve extension was lost. Please refresh the page and try again.',
    },
    insufficientStorage: {
      title: 'Recording paused due to low storage',
      message:
        'Your device ran low on storage, so we paused the recording to keep what was captured. Free up space to resume, or finish to generate your note.',
    },
    ehrIntegrationNotAvailable: {
      title: 'EHR Integration Not Available',
      message:
        'This EHR vendor requires browser extension integration which is not available in this context.',
    },
    appointmentAlreadySigned: {
      title: 'Note already signed',
      message:
        'This note is already signed, so the appointment can no longer be recorded or regenerated.',
    },
  },
  microphone: {
    noAudioDetectedCompact: 'Audio not detected.',
    noAudioDetectedFull:
      'Audio not detected. Please select a different audio source to start.',
    permissionsDeniedTitle: 'Microphone permissions denied',
    permissionsDeniedMessage:
      'Please enable microphone permissions in your browser settings to enable audio input.',
    noDevicesTitle: 'Microphone devices required',
    noDevicesMessage:
      'There are no microphone devices connected to your system, please grant microphone permissions or retry.',
    retryPermission: 'Retry Permission',
    permissionsRequiredTitle: 'Microphone permissions required',
    permissionsRequiredMessage:
      'Please grant microphone permissions to enable audio input.',
    requestPermission: 'Request Permission',
  },
};

/**
 * Canonical shape of a translation bundle, derived from {@link enMessages}.
 * Every non-English locale file must conform to this type, so the compiler
 * enforces that every English key has a translation in every other locale.
 */
export type Messages = typeof enMessages;
